import React, { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import _ from 'lodash'
import Well from '../common/Well'
import DismissibleAlert from '../common/DismissibleAlert'
import Password from './form/Password'
import { saveAccount, clearLoginError } from '../../actions/login'
import { validatePassword } from '../../utils/validators'

import styles from './Form.module.css'

function Form({ account, inProgress, error, save, clearError }) {
  const [formState, setFormState] = useState(null)
  const [localErrors, setLocalErrors] = useState({})

  useEffect(() => {
    if (account) {
      setFormState({
        ..._.clone(account.attributes),
        oldPassword: '',
        password: '',
        verifyPassword: ''
      })
    }
  }, [])

  if (!formState) return null

  const handleChange = fieldName => event => {
    const input = event.target
    const val = input.type === 'checkbox' ? input.checked : input.value
    setFormState(prev => ({ ...prev, [fieldName]: val }))
    // Clear local validation error for this field
    if (localErrors[fieldName]) {
      setLocalErrors(prev => { const next = { ...prev }; delete next[fieldName]; return next })
    }
    // Clear any redux API errors on input change
    if (error && (error[fieldName] || error.message)) {
      clearError()
    }
  }

  const handleSubmit = event => {
    event.preventDefault()
    const { valid, error: validationError } = validatePassword(formState, true)
    if (!valid) {
      setLocalErrors(validationError)
      return
    }
    setLocalErrors({})
    save(account, formState)
  }

  // Merge local validation errors with API errors; API errors override local ones
  const mergedErrors = { ...localErrors, ..._.omit(error, ['message', 'status', 'errors']) }
  const generalError = error && error.message ? error.message : null

  const submitButtonText = inProgress
    ? <span><i className="fa fa-circle-o-notch fa-spin" /> Processing...</span>
    : 'Save'

  return (
    <div id="content">
      <header>
        <h2>
          Account Settings{' '}
          <span className={styles.email}>({formState.email})</span>
        </h2>
      </header>
      <form className={`smart-form ${styles.form}`} noValidate>
        <Well>
          <header>Change Password</header>
          {generalError && (
            <DismissibleAlert
              type="danger"
              style={{ margin: '10px 15px 0' }}
              resetKey={error}
            >
              {generalError}
            </DismissibleAlert>
          )}
          <Password
            data={formState}
            error={mergedErrors}
            onChangeFactory={handleChange}
            disabled={inProgress}
          />
          <footer>
            <button
              type="submit"
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={inProgress}
            >
              {submitButtonText}
            </button>
          </footer>
        </Well>
      </form>
    </div>
  )
}

function mapStateToProps({ login }) {
  const currentUser = login.get('currentUser')
  let account = null
  if (currentUser) {
    if (typeof currentUser.toJSON === 'function') {
      // Immutable Entity (e.g. fresh login response)
      account = currentUser.toJSON()
    } else {
      // Plain localStorage object: { type, id, username, retailerType }
      // username is the email address for all account types
      account = {
        id: currentUser.id,
        type: currentUser.type,
        attributes: {
          email: currentUser.username,
          username: currentUser.username,
          retailerType: currentUser.retailerType,
        }
      }
    }
  }
  return {
    account,
    inProgress: login.get('inProgress'),
    error: login.get('error') || {}
  }
}

function mapDispatchToProps(dispatch) {
  return {
    save(account, data) {
      account.attributes = _.pick(data, [
        'password',
        'verifyPassword',
        'oldPassword'
      ])
      dispatch(saveAccount(account))
    },
    clearError() {
      dispatch(clearLoginError())
    }
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(Form)
